import arrayUnique from 'array-unique';
import clamp from 'clamp';

import type { ContentRef } from '@gitbook/api-client';
import * as api from '@gitbook/api-client';
import {
    contentReferencesAreEqual,
    validateAnyContentReference,
    validateContentReference,
} from '@gitbook/app-content-refs';

import type {
    CheckboxColumnDefinition,
    ContentRefColumnDefinition,
    FilesColumnDefinition,
    ImageColumnDefinition,
    NumberColumnDefinition,
    RatingColumnDefinition,
    SelectColumnDefinition,
    TableColumnType,
    TableDefinitionImageRecord,
    TableRecordValue,
    TextColumnDefinition,
    UsersColumnDefinition,
} from '../../types';
import { createTextFragment } from '../../utils';
import { getContentRefFromImageValue, isExpandedImageRecordFormat } from '../views/cards';
import type { ColumnValidationSpec } from './types';

/** Validation for Text columns. */
const textValidation: ColumnValidationSpec<string, TextColumnDefinition> = {
    fragmentKeys: (_key, value) => [value],
    normalize: (key, value, colDef, node) => {
        if (typeof value === 'string' && node.hasFragment(value)) {
            return;
        }

        return (change) => {
            const fragmentKey = `${key}-${colDef.id}`;

            // If no value is set (null or undefined), use an empty string.
            // If the value is the default value, make sure the fragment is created.
            // Otherwise, try to convert the previous value to a string.
            const convertedValue =
                value === undefined || value === null || value === fragmentKey ? '' : String(value);

            if (!node.hasFragment(fragmentKey)) {
                change.insertFragmentByKey(
                    node.key,
                    createTextFragment({ fragment: fragmentKey, text: convertedValue })
                );
            }

            return fragmentKey;
        };
    },
    normalizeDefinition: (colDef) => {
        let result = colDef;

        if (
            typeof result.textAlignment !== 'string' ||
            (result.textAlignment !== 'center' &&
                result.textAlignment !== 'left' &&
                result.textAlignment !== 'right')
        ) {
            result = { ...result, textAlignment: 'left' };
        }

        return result === colDef ? undefined : result;
    },
    compare: (a, b) => {
        return a === b;
    },
};

/** Validation for Number columns. */
const numberValidation: ColumnValidationSpec<number | null, NumberColumnDefinition> = {
    normalize: (_key, value, _colDef, node) => {
        if (typeof value === 'number' || value === null) {
            return;
        }

        // from a string
        if (typeof value === 'string') {
            // Value is stored in a fragment, get the text value.
            if (node.fragments.has(value)) {
                value = node.fragments.get(value)?.getText();
            }

            if (Number.isNaN(Number(value))) {
                return null;
            }

            const float = Number.parseFloat(value);

            // check if integer
            if (float % 1 === 0) {
                return Number.parseInt(value, 10);
            }

            return float;
        }

        // from a boolean
        if (typeof value === 'boolean') {
            return value ? 1 : 0;
        }

        return null;
    },
    compare: (a, b) => {
        return a === b;
    },
};

/** Validation for Checkbox. */
const checkboxValidation: ColumnValidationSpec<boolean, CheckboxColumnDefinition> = {
    normalize: (_key, value) => {
        if (typeof value === 'boolean') {
            return;
        }

        // from a number
        if (typeof value === 'number') {
            return value > 0;
        }

        // from a string
        if (typeof value === 'string') {
            return Boolean(value);
        }

        return false;
    },
    compare: (a, b) => {
        return a === b;
    },
};

/** Validation for Select */
const selectValidation: ColumnValidationSpec<string[], SelectColumnDefinition> = {
    normalize: (_key, value, colDef) => {
        if (Array.isArray(value)) {
            const validated = value.filter((item) =>
                colDef.options.some((option) => option.value === item)
            );
            return validated.length === value.length ? undefined : validated;
        }

        return [];
    },
    normalizeDefinition: (def) => {
        let result = def;

        if (typeof result.multiple !== 'boolean') {
            result = { ...result, multiple: false };
        }

        if (!Array.isArray(result.options)) {
            result = { ...result, options: [] };
        }

        return result;
    },
    compare: (a, b) => {
        if (a.length !== b.length) {
            return false;
        }

        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) {
                return false;
            }
        }

        return true;
    },
};

/** Validation for Files */
const filesValidation: ColumnValidationSpec<string[], FilesColumnDefinition> = {
    normalize: (_key, value) => {
        if (Array.isArray(value)) {
            return;
        }

        return [];
    },
    compare: (a, b) => {
        if (a.length !== b.length) {
            return false;
        }

        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) {
                return false;
            }
        }

        return true;
    },
};

/** Validation for Users */
const usersValidation: ColumnValidationSpec<string[], UsersColumnDefinition> = {
    normalize: (_key, value, colDef) => {
        if (Array.isArray(value)) {
            let users = arrayUnique(value.filter((v) => typeof v === 'string'));

            if (!colDef.multiple) {
                users = users.slice(-1);
            }

            return users.length !== value.length ? users : undefined;
        }

        return [];
    },
    normalizeDefinition: (colDef) => {
        let result = colDef;

        if (typeof result.multiple !== 'boolean') {
            result = { ...result, multiple: true };
        }

        return result === colDef ? undefined : result;
    },
    compare: (a, b) => {
        if (a.length !== b.length) {
            return false;
        }

        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) {
                return false;
            }
        }

        return true;
    },
};

/** Validation for Rating. */
const ratingValidation: ColumnValidationSpec<number | null, RatingColumnDefinition> = {
    normalize: (key, value, colDef, node) => {
        const result = columnSpecs.number.normalize(key, value, colDef, node);

        if (typeof result === 'number') {
            return clamp(result, 0, colDef.max);
        }

        return result;
    },
    normalizeDefinition: (colDef) => {
        let result = colDef;

        if (typeof result.max !== 'number' || result.max <= 0 || result.max > 5) {
            result = { ...result, max: 5 };
        }

        return result === colDef ? undefined : result;
    },
    compare: (a, b) => {
        return a === b;
    },
};

/** Validation for Content Ref. */
const contentRefValidation: ColumnValidationSpec<ContentRef | null, ContentRefColumnDefinition> = {
    normalize: (_key, value) => {
        if (!validateAnyContentReference(value)) {
            return null;
        }

        return value;
    },
    compare: contentReferencesAreEqual,
};

const validateContentRef = validateContentReference(['file', 'url']);

/** Validation for Image */
const imageValidation: ColumnValidationSpec<
    TableDefinitionImageRecord | null,
    ImageColumnDefinition
> = {
    normalize: (_key, value) => {
        if (value === null || value === undefined) {
            return null;
        }

        // Extract the ContentRef from either format
        const contentRef = isExpandedImageRecordFormat(value) ? value.ref : value;

        // Validate the ContentRef once
        if (!validateContentRef(contentRef)) {
            return null;
        }

        // Handle new format with ref, objectFit, and alt
        if (isExpandedImageRecordFormat(value)) {
            if (
                value.objectFit &&
                !Object.values(api.CardsImageObjectFit).includes(value.objectFit)
            ) {
                // Return normalized version without invalid objectFit
                return {
                    ref: value.ref,
                    ...(value.alt && typeof value.alt === 'string' ? { alt: value.alt } : {}),
                };
            }

            // Validate alt if present
            if (value.alt && typeof value.alt !== 'string') {
                return {
                    ref: value.ref,
                    ...(value.objectFit ? { objectFit: value.objectFit } : {}),
                };
            }
        }

        // Handle legacy format (direct ContentRef)
        return value;
    },
    compare: (a, b) => {
        if (a === null && b === null) {
            return true;
        }
        if (a === null || b === null) {
            return false;
        }

        // Extract ContentRefs from both values for comparison
        const refA = getContentRefFromImageValue(a);
        const refB = getContentRefFromImageValue(b);

        // Compare the content references
        if (!contentReferencesAreEqual(refA, refB)) {
            return false;
        }

        // If both are new format, also compare objectFit and alt
        if (isExpandedImageRecordFormat(a) && isExpandedImageRecordFormat(b)) {
            return (
                (a.objectFit || api.CardsImageObjectFit.Cover) ===
                    (b.objectFit || api.CardsImageObjectFit.Cover) && a.alt === b.alt
            );
        }

        // If one is new format and one is legacy, they're equal if refs match
        // (objectFit defaults to Cover for legacy format)
        return true;
    },
};

/**
 * Validation for each column type.
 */
export const columnSpecs: { [type in TableColumnType]: ColumnValidationSpec<TableRecordValue> } = {
    text: textValidation,
    number: numberValidation,
    checkbox: checkboxValidation,
    select: selectValidation,
    files: filesValidation,
    users: usersValidation,
    rating: ratingValidation,
    'content-ref': contentRefValidation,
    image: imageValidation,
};
